import axios, { AxiosError } from 'axios';
import { IPlan } from 'types/Plan';
import {
  ITutorUserCreateOrContractPlanResponse,
  ITutorUserSubscribeToPlan,
} from 'types/TutorUser';
import { maskCurrencyFormat } from 'util/masks';

import { Api } from './api/axios.service';
import { authHeader } from './header.service';

const BASE_URL = '/tutor-plan';

/**
 * List all available plans
 *
 * @return {*}  {Promise<IPlan[]>}
 */
const getAllPlans = async (): Promise<IPlan[]> => {
  try {
    const response = await Api.get<IPlan[]>('/plan');
    return response.data?.map((plan) => ({
      ...plan,
      id: plan._id,
      maskedPrice: maskCurrencyFormat(plan.price),
    }));
  } catch (err) {
    throw new Error('Não foi possível carregar os planos');
  }
};

interface ICredentials {
  pjbankCreditCardTokenUrl: string;
}
/**
 * Get pjbank credit card token url
 *
 * @return {*}  {Promise<ICredentials>}
 */
const getCreditCardTokenUrl = async (): Promise<ICredentials> => {
  const { data } = await Api.get<ICredentials>(`${BASE_URL}/get-token`);

  return Promise.resolve(data);
};

interface CreatePjbankCreditCardTokenResponse {
  msg: string;
  cartao_validado: string;
  status: string;
  cartao_truncado: string;
  token_cartao: string;
  bandeira: string;
}

/**
 * Create a credit card token on pjbank
 *
 * @param {string} creditCardNumber
 * @return {*}  {Promise<string>}
 */
const createPjbankCreditCardToken = async (
  creditCardNumber: string
): Promise<string> => {
  const { pjbankCreditCardTokenUrl } = await getCreditCardTokenUrl();

  const { data } = await axios.post<CreatePjbankCreditCardTokenResponse>(
    pjbankCreditCardTokenUrl,
    {
      numero_cartao: creditCardNumber,
    }
  );

  if (data.status !== '201') {
    throw new Error('Cartão de crédito inválido');
  }

  return data.token_cartao;
};

/**
 * Subscribe user to provided plan
 *
 * @param {ITutorUserSubscribeToPlan} values
 * @return {*}  {Promise<ITutorUserCreateOrContractPlanResponse>}
 */
const subscrilbeToPlan = async ({
  cardHash,
  planId,
  ticket,
  holderName,
  cpf,
  billingAddress
}: any): Promise<ITutorUserCreateOrContractPlanResponse> => {
  try {
    const { data } = await Api.post<ITutorUserCreateOrContractPlanResponse>(
      `${BASE_URL}/create`,
      {
        cardHash,
        plan: planId,
        ticket,
        holderName,
        cpf,
        billingAddress
      },
      {
        headers: authHeader(),
      }
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível assinar o plano selecionado.'
    );
  }
};

/**
 * Cancel a plan by providing tutorPlanId
 *
 * @param {string} tutorPlanId
 * @return {*}  {Promise<void>}
 */
const cancelPlan = async (tutorPlanId: string | undefined): Promise<void> => {
  try {
    await Api.put(
      `${BASE_URL}/disable/${tutorPlanId}`,
      {},
      {
        headers: authHeader(),
      }
    );
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível cancelar o plano selecionado.'
    );
  }
};

interface IGetCouponParams {
  ticketName: string;
  planId: string;
}
export interface ICouponValues {
  originalValue: number;
  discountValue: string;
  newValue: number;
  formatedFinalValue: string;
}
/**
 * Apply a coupon returning new value with discout
 *
 * @param {IGetCouponParams} params
 * @return {*}  {Promise<ICouponValues>}
 */
const getCouponByName = async (
  params: IGetCouponParams
): Promise<ICouponValues> => {
  try {
    const { data } = await Api.post<any>('/ticket/getValues', params);
    return data.formated;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível aplicar o cupom.'
    );
  }
};

export const TutorUserPlanService = {
  getAllPlans,
  subscrilbeToPlan,
  cancelPlan,
  getCouponByName,
  createPjbankCreditCardToken,
};
